import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui";
import { useT } from "../i18n/index.js";

/**
 * The question asked before something that cannot be taken back.
 *
 * Extracted from the list-delete dialog, which was the first thing in the app
 * heavy enough to need one and — until a second caller appeared — the only
 * place that knew how to do it: the portal, the escape key, the locked page
 * behind it, and where the focus lands.
 *
 * **Focus goes to the dialog, not to the destructive button.** What this asks
 * for is reading, and a focused «Удалить» invites an answer before the question
 * has been read.
 *
 * Reserved for the heavy end of the scale. Most destructive things in this app
 * are two presses on the button itself, which is quieter and enough; a dialog
 * is for what takes something away from the other person too.
 */
export function ConfirmDialog({
  title,
  confirmLabel,
  children,
  onCancel,
  onConfirm,
}: {
  title: string;
  confirmLabel: string;
  /** The consequences, in the plainest words available. */
  children: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const t = useT();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    dialogRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      cancelRef.current();
    };

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return createPortal(
    <>
      {/* Black rather than a token: dimming has to read as shade in both
          themes, and every surface token flips between them. */}
      <div onClick={onCancel} aria-hidden="true" className="fixed inset-0 z-50 bg-black/45" />

      <div className="pad-safe-x pointer-events-none fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overscroll-contain px-4 py-8">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="pointer-events-auto flex w-full max-w-md flex-col gap-4 rounded-card bg-surface p-5 shadow-soft outline-none"
        >
          <h2 id={titleId} className="font-display text-[1.0625rem] text-neg">
            {title}
          </h2>

          <div className="flex flex-col gap-2 text-[0.9375rem] leading-snug text-ink-soft">
            {children}
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="secondary" full onClick={onCancel}>
              {t("common.cancel")}
            </Button>
            <Button type="button" variant="danger" full onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
